import React, { useState, useEffect } from 'react';

interface Family {
  id: string;
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  secondaryContactFirstName?: string;
  secondaryContactLastName?: string;
  secondaryContactEmail?: string;
  secondaryContactPhone?: string;
  city?: string;
  state?: string;
}

interface RanchGirl {
  id: string;
  firstName: string;
  lastName: string;
  skillLevel: string;
  graduationYear: number;
  boardPosition?: string;
  family?: {
    familyName: string;
    primaryContactPhone: string;
    primaryContactEmail: string;
  };
}

interface BoardMember {
  id: string;
  firstName: string;
  lastName: string;
  position: string;
  email?: string;
  phone?: string;
  displayOrder: number;
}

interface AlumniMember {
  id: string;
  firstName: string;
  lastName: string;
  category: 'alumni-mom' | 'alumni-dad' | 'ranch-girl';
  email?: string;
  phone?: string;
  isActingBigSister: boolean;
}

const CATEGORY_LABELS = {
  'alumni-mom': 'Alumni Mom',
  'alumni-dad': 'Alumni Dad',
  'ranch-girl': 'Ranch Girl'
};

export default function DirectoryView() {
  const [activeTab, setActiveTab] = useState<'families' | 'ranchgirls' | 'board' | 'alumni'>('families');
  const [families, setFamilies] = useState<Family[]>([]);
  const [ranchGirls, setRanchGirls] = useState<RanchGirl[]>([]);
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
  const [alumniMembers, setAlumniMembers] = useState<AlumniMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch families
      const familiesResponse = await fetch('/api/families/directory', { headers });
      if (familiesResponse.ok) {
        const familiesData = await familiesResponse.json();
        if (familiesData.success) {
          setFamilies(familiesData.data);
        }
      }

      // Fetch ranch girls
      const ranchGirlsResponse = await fetch('/api/ranchgirls/directory', { headers });
      if (ranchGirlsResponse.ok) {
        const ranchGirlsData = await ranchGirlsResponse.json();
        if (ranchGirlsData.success) {
          setRanchGirls(ranchGirlsData.data);
        }
      }

      // Fetch board members (public endpoint)
      const boardResponse = await fetch('/api/boardmembers');
      if (boardResponse.ok) {
        const boardData = await boardResponse.json();
        if (boardData.success) {
          setBoardMembers(boardData.data);
        }
      }

      // Fetch alumni members (public endpoint)
      const alumniResponse = await fetch('/api/alumni');
      if (alumniResponse.ok) {
        const alumniData = await alumniResponse.json();
        if (alumniData.success) {
          setAlumniMembers(alumniData.data);
        }
      }
    } catch (error) {
      console.error('Error fetching directory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    let csvContent = '';
    let filename = '';

    if (activeTab === 'families') {
      csvContent = 'Family Name,Primary Contact,Email,Phone,Secondary Contact,Secondary Email,Secondary Phone,City,State\n';
      filteredFamilies.forEach(family => {
        csvContent += `"${family.familyName}","${family.primaryContactFirstName} ${family.primaryContactLastName}","${family.primaryContactEmail}","${family.primaryContactPhone}",`;
        csvContent += `"${family.secondaryContactFirstName || ''} ${family.secondaryContactLastName || ''}","${family.secondaryContactEmail || ''}","${family.secondaryContactPhone || ''}","${family.city || ''}","${family.state || ''}"\n`;
      });
      filename = 'families-directory.csv';
    } else if (activeTab === 'ranchgirls') {
      csvContent = 'Ranch Girl Name,Skill Level,Board Position,Graduation Year,Family,Family Phone,Family Email\n';
      filteredRanchGirls.forEach(girl => {
        csvContent += `"${girl.firstName} ${girl.lastName}","${girl.skillLevel}","${girl.boardPosition || ''}","${girl.graduationYear}",`;
        csvContent += `"${girl.family?.familyName || ''}","${girl.family?.primaryContactPhone || ''}","${girl.family?.primaryContactEmail || ''}"\n`;
      });
      filename = 'ranch-girls-directory.csv';
    } else if (activeTab === 'board') {
      csvContent = 'Name,Position,Email,Phone\n';
      filteredBoardMembers.forEach(member => {
        csvContent += `"${member.firstName} ${member.lastName}","${member.position}","${member.email || ''}","${member.phone || ''}"\n`;
      });
      filename = 'board-members-directory.csv';
    } else {
      csvContent = 'Name,Category,Email,Phone,Big Sister\n';
      filteredAlumniMembers.forEach(member => {
        csvContent += `"${member.firstName} ${member.lastName}","${CATEGORY_LABELS[member.category]}","${member.email || ''}","${member.phone || ''}","${member.isActingBigSister ? 'Yes' : 'No'}"\n`;
      });
      filename = 'alumni-members-directory.csv';
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const filteredFamilies = families.filter(family =>
    family.familyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${family.primaryContactFirstName} ${family.primaryContactLastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    family.primaryContactEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredRanchGirls = ranchGirls.filter(girl =>
    `${girl.firstName} ${girl.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    girl.family?.familyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    girl.skillLevel.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredBoardMembers = boardMembers.filter(member =>
    `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.position.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAlumniMembers = alumniMembers.filter(member =>
    `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    CATEGORY_LABELS[member.category].toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-6 lg:py-10">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Ranch Directory</h1>
        <p className="text-sm sm:text-base text-gray-600">Contact information for families, ranch girls, and board members</p>
      </div>

      {/* Tabs and Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        {/* Tabs - Horizontal scroll on mobile */}
        <div className="border-b border-gray-200 overflow-x-auto">
          <div className="flex min-w-max sm:min-w-0 p-2 gap-2">
            <button
              onClick={() => setActiveTab('families')}
              className={`flex-shrink-0 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'families'
                  ? 'bg-ggaa-green text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>Families</span>
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === 'families' ? 'bg-white text-ggaa-green' : 'bg-ggaa-green text-white'
              }`}>
                {families.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('ranchgirls')}
              className={`flex-shrink-0 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'ranchgirls'
                  ? 'bg-ggaa-green text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>Ranch Girls</span>
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === 'ranchgirls' ? 'bg-white text-ggaa-green' : 'bg-ggaa-green text-white'
              }`}>
                {ranchGirls.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('board')}
              className={`flex-shrink-0 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'board'
                  ? 'bg-ggaa-green text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>Board</span>
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === 'board' ? 'bg-white text-ggaa-green' : 'bg-ggaa-green text-white'
              }`}>
                {boardMembers.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('alumni')}
              className={`flex-shrink-0 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'alumni'
                  ? 'bg-ggaa-green text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>Alumni</span>
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === 'alumni' ? 'bg-white text-ggaa-green' : 'bg-ggaa-green text-white'
              }`}>
                {alumniMembers.length}
              </span>
            </button>
          </div>
        </div>

        {/* Search and Download */}
        <div className="p-4 space-y-3">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent text-sm"
          />
          <button
            onClick={downloadCSV}
            className="w-full sm:w-auto bg-ggaa-green text-white px-4 py-2 rounded-lg hover:bg-ggaa-green/90 transition-colors flex items-center justify-center text-sm font-medium"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download CSV
          </button>
        </div>
      </div>

      {/* Families Tab */}
      {activeTab === 'families' && (
        <>
          {filteredFamilies.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              {searchTerm ? 'No families found matching your search' : 'No families in directory'}
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Family
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Primary Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Secondary Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredFamilies.map((family) => (
                        <tr key={family.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{family.familyName}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {family.primaryContactFirstName} {family.primaryContactLastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              📧 {family.primaryContactEmail}
                            </div>
                            <div className="text-sm text-gray-500">
                              📱 {family.primaryContactPhone}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {family.secondaryContactFirstName ? (
                              <>
                                <div className="text-sm text-gray-900">
                                  {family.secondaryContactFirstName} {family.secondaryContactLastName}
                                </div>
                                {family.secondaryContactEmail && (
                                  <div className="text-sm text-gray-500">
                                    📧 {family.secondaryContactEmail}
                                  </div>
                                )}
                                {family.secondaryContactPhone && (
                                  <div className="text-sm text-gray-500">
                                    📱 {family.secondaryContactPhone}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="text-sm text-gray-400">—</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {family.city && family.state ? `${family.city}, ${family.state}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {filteredFamilies.map((family) => (
                  <div key={family.id} className="bg-white rounded-lg shadow-lg p-4 border border-ggaa-green/20">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{family.familyName}</h3>
                    
                    {/* Primary Contact */}
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Primary Contact</p>
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {family.primaryContactFirstName} {family.primaryContactLastName}
                      </p>
                      <a href={`mailto:${family.primaryContactEmail}`} className="text-sm text-ggaa-green hover:underline block mb-1">
                        📧 {family.primaryContactEmail}
                      </a>
                      <a href={`tel:${family.primaryContactPhone}`} className="text-sm text-gray-600 hover:text-ggaa-green block">
                        📱 {family.primaryContactPhone}
                      </a>
                    </div>

                    {/* Secondary Contact */}
                    {family.secondaryContactFirstName && (
                      <div className="mb-4 pt-4 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Secondary Contact</p>
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          {family.secondaryContactFirstName} {family.secondaryContactLastName}
                        </p>
                        {family.secondaryContactEmail && (
                          <a href={`mailto:${family.secondaryContactEmail}`} className="text-sm text-ggaa-green hover:underline block mb-1">
                            📧 {family.secondaryContactEmail}
                          </a>
                        )}
                        {family.secondaryContactPhone && (
                          <a href={`tel:${family.secondaryContactPhone}`} className="text-sm text-gray-600 hover:text-ggaa-green block">
                            📱 {family.secondaryContactPhone}
                          </a>
                        )}
                      </div>
                    )}

                    {/* Location */}
                    {family.city && family.state && (
                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Location</p>
                        <p className="text-sm text-gray-600">{family.city}, {family.state}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Ranch Girls Tab */}
      {activeTab === 'ranchgirls' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRanchGirls.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow p-8 text-center text-gray-500 text-sm sm:text-base">
              {searchTerm ? 'No ranch girls found matching your search' : 'No ranch girls in directory'}
            </div>
          ) : (
            filteredRanchGirls.map((girl) => (
              <div key={girl.id} className="bg-white rounded-lg shadow-lg p-4 sm:p-6 border border-ggaa-green/20 hover:shadow-xl transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                      {girl.firstName} {girl.lastName}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">{girl.family?.familyName}</p>
                    {girl.boardPosition && (
                      <p className="text-xs sm:text-sm font-medium text-ggaa-green mt-1">
                        🏛️ {girl.boardPosition}
                      </p>
                    )}
                  </div>
                  <span className="flex-shrink-0 ml-2 px-2 py-1 bg-ggaa-cream text-ggaa-green text-xs font-medium rounded-full whitespace-nowrap">
                    {girl.skillLevel}
                  </span>
                </div>
                
                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="flex items-center text-gray-600">
                    <span className="font-medium w-20 sm:w-24">Graduation:</span>
                    <span>{girl.graduationYear}</span>
                  </div>
                  
                  {girl.family && (
                    <div className="pt-3 mt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Family Contact</p>
                      {girl.family.primaryContactEmail && (
                        <a href={`mailto:${girl.family.primaryContactEmail}`} className="text-xs sm:text-sm text-ggaa-green hover:underline block mb-1 break-all">
                          📧 {girl.family.primaryContactEmail}
                        </a>
                      )}
                      {girl.family.primaryContactPhone && (
                        <a href={`tel:${girl.family.primaryContactPhone}`} className="text-xs sm:text-sm text-gray-600 hover:text-ggaa-green block">
                          📱 {girl.family.primaryContactPhone}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Board Members Tab */}
      {activeTab === 'board' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBoardMembers.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow p-8 text-center text-gray-500 text-sm sm:text-base">
              {searchTerm ? 'No board members found matching your search' : 'No board members in directory'}
            </div>
          ) : (
            filteredBoardMembers.map((member) => (
              <div key={member.id} className="bg-white rounded-lg shadow-lg p-4 sm:p-6 border border-ggaa-green/20 hover:shadow-xl transition-shadow">
                <div className="mb-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900">
                    {member.firstName} {member.lastName}
                  </h3>
                  <p className="text-xs sm:text-sm font-medium text-ggaa-green">{member.position}</p>
                </div>
                
                <div className="space-y-2 text-xs sm:text-sm">
                  {member.email && (
                    <div className="flex items-start text-gray-600">
                      <span className="mr-2 flex-shrink-0">📧</span>
                      <a href={`mailto:${member.email}`} className="hover:text-ggaa-green hover:underline break-all">
                        {member.email}
                      </a>
                    </div>
                  )}
                  {member.phone && (
                    <div className="flex items-center text-gray-600">
                      <span className="mr-2 flex-shrink-0">📱</span>
                      <a href={`tel:${member.phone}`} className="hover:text-ggaa-green hover:underline">
                        {member.phone}
                      </a>
                    </div>
                  )}
                  {!member.email && !member.phone && (
                    <p className="text-gray-400 italic text-xs sm:text-sm">No contact information available</p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Alumni Tab */}
      {activeTab === 'alumni' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAlumniMembers.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow p-8 text-center text-gray-500 text-sm sm:text-base">
              {searchTerm ? 'No alumni members found matching your search' : 'No alumni members in directory'}
            </div>
          ) : (
            filteredAlumniMembers.map((member) => (
              <div key={member.id} className="bg-white rounded-lg shadow-lg p-4 sm:p-6 border border-ggaa-green/20 hover:shadow-xl transition-shadow">
                <div className="mb-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900">
                    {member.firstName} {member.lastName}
                  </h3>
                  <p className="text-xs sm:text-sm font-medium text-ggaa-green">{CATEGORY_LABELS[member.category]}</p>
                  {member.isActingBigSister && (
                    <span className="inline-flex items-center mt-2 px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓ Acting Big Sister
                    </span>
                  )}
                </div>
                
                <div className="space-y-2 text-xs sm:text-sm">
                  {member.email && (
                    <div className="flex items-start text-gray-600">
                      <span className="mr-2 flex-shrink-0">📧</span>
                      <a href={`mailto:${member.email}`} className="hover:text-ggaa-green hover:underline break-all">
                        {member.email}
                      </a>
                    </div>
                  )}
                  {member.phone && (
                    <div className="flex items-center text-gray-600">
                      <span className="mr-2 flex-shrink-0">📱</span>
                      <a href={`tel:${member.phone}`} className="hover:text-ggaa-green hover:underline">
                        {member.phone}
                      </a>
                    </div>
                  )}
                  {!member.email && !member.phone && (
                    <p className="text-gray-400 italic text-xs sm:text-sm">No contact information available</p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
} 